import { useState, useMemo, useCallback } from 'react';
import { checkLevelUp } from '../../../config/levelsConfig';
import { LEVEL_UP_MODAL_DELAY } from '../constants';

/**
 * Points come from the API as a single derived number. The backend computes them
 * from state (completed assignments + item ratings) using the four rules shown in
 * the app's 积分说明 box — see backend/features/gamification/points.
 *
 * This used to sum six stored coin sources client-side, which meant the number on
 * screen could disagree with the database. There is now one number and one source.
 *
 * @param {Object} streakData - Streak data from API ({ current_streak, points })
 * @returns {{
 *   totalEarnedCoins: number,
 *   availableCoins: number,
 *   levelUpData: Object | null,
 *   handleQuizXpEarned: (xp: number) => void,
 *   clearLevelUp: () => void
 * }}
 */
export function useStudentCoins(streakData) {
  // Points earned during this session that the server hasn't been re-queried for,
  // so a child sees the number move the moment they earn it.
  const [sessionPoints, setSessionPoints] = useState(0);
  const [levelUpData, setLevelUpData] = useState(null);

  const basePoints = useMemo(() => streakData?.points || 0, [streakData]);

  const totalPoints = basePoints + sessionPoints;

  const handleQuizXpEarned = useCallback((amount) => {
    setSessionPoints(prev => {
      const next = prev + amount;

      const levelUp = checkLevelUp(basePoints + prev, basePoints + next);
      if (levelUp) {
        setTimeout(() => setLevelUpData(levelUp), LEVEL_UP_MODAL_DELAY);
      }

      return next;
    });
  }, [basePoints]);

  const clearLevelUp = useCallback(() => {
    setLevelUpData(null);
  }, []);

  return {
    // Kept as two names because callers distinguish them, but with no prize store
    // in use there is nothing to spend, so earned and available are the same number.
    totalEarnedCoins: totalPoints,
    availableCoins: totalPoints,
    levelUpData,
    handleQuizXpEarned,
    clearLevelUp,
  };
}

export default useStudentCoins;
